import React, { useState } from 'react';
import { useGetStudentLearningGoalCoverageQuery } from '../../store/api/localApi';
import type { LearningGoalTheme } from '../../types';
import { THEME_LABELS, THEME_COLORS, groupByThemeAndAim } from './themes';

interface Props {
  studentId: string;
}

/** A small SVG donut showing evidenced/total for one theme. */
const ProgressRing: React.FC<{ evidenced: number; total: number; colorClass: string }> = ({
  evidenced,
  total,
  colorClass,
}) => {
  const r = 18;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? evidenced / total : 0;
  return (
    <svg viewBox="0 0 44 44" className="h-12 w-12 -rotate-90">
      <circle cx="22" cy="22" r={r} fill="none" strokeWidth="4" className="stroke-gray-200" />
      <circle
        cx="22"
        cy="22"
        r={r}
        fill="none"
        strokeWidth="4"
        strokeLinecap="round"
        className={`${colorClass} transition-all`}
        stroke="currentColor"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
      />
    </svg>
  );
};

/**
 * Aistéar learning-goal coverage for a single child. Under-6 only: the backend
 * returns `eligible: false` for an over-6 child (and the StudentDetail host also
 * gates this section on the child's DOB), in which case we render nothing.
 *
 * UX: a summary-first view. Four theme cards each show a progress ring
 * (evidenced / total goals). Tapping a card expands its aims + goals, so the
 * full 48-goal list is available on demand rather than dumped up front.
 */
const LearningGoalCoverage: React.FC<Props> = ({ studentId }) => {
  const { data } = useGetStudentLearningGoalCoverageQuery({ studentId });
  const [openTheme, setOpenTheme] = useState<LearningGoalTheme | null>(null);

  if (!data?.eligible) return null;

  const groups = groupByThemeAndAim(data.coverage);

  const totalGoals = data.coverage.length;
  const totalEvidenced = data.coverage.filter((g) => g.observationCount > 0).length;

  return (
    <div data-testid="learning-goal-coverage" className="mt-8">
      <div className="flex items-baseline justify-between">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Aistéar learning goals</h3>
        <span data-testid="lg-coverage-total" className="text-sm text-gray-500">
          {totalEvidenced} of {totalGoals} evidenced
        </span>
      </div>
      <p className="mt-1 text-sm text-gray-500">
        Progress across the four themes, evidenced by tagged observations. Tap a theme to see its
        goals.
      </p>

      {groups.length === 0 ? (
        <p className="mt-4 text-sm italic text-gray-400">No learning goals defined yet.</p>
      ) : (
        <>
          {/* Theme summary cards */}
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {groups.map((group) => {
              const goals = group.aims.flatMap((a) => a.goals);
              const evidenced = goals.filter((g) => g.observationCount > 0).length;
              const colors = THEME_COLORS[group.theme];
              const isOpen = openTheme === group.theme;
              return (
                <button
                  key={group.theme}
                  type="button"
                  data-testid={`lg-theme-card-${group.theme}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpenTheme(isOpen ? null : group.theme)}
                  className={`flex flex-col items-center rounded-lg border p-3 text-center transition ${
                    isOpen
                      ? `${colors.bg} border-current ${colors.text}`
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="relative flex items-center justify-center">
                    <ProgressRing
                      evidenced={evidenced}
                      total={goals.length}
                      colorClass={colors.ring}
                    />
                    <span className="absolute text-xs font-semibold text-gray-700">
                      {evidenced}/{goals.length}
                    </span>
                  </div>
                  <span className={`mt-2 text-xs font-medium ${colors.text}`}>
                    {THEME_LABELS[group.theme]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Drill-down: goals for the open theme */}
          {openTheme &&
            groups
              .filter((g) => g.theme === openTheme)
              .map((group) => (
                <div
                  key={group.theme}
                  data-testid={`lg-theme-detail-${group.theme}`}
                  className="mt-4 rounded-lg border border-gray-200 p-4"
                >
                  <h4 className={`text-sm font-semibold ${THEME_COLORS[group.theme].text}`}>
                    {THEME_LABELS[group.theme]}
                  </h4>
                  <div className="mt-2 space-y-3">
                    {group.aims.map((aim) => (
                      <div key={aim.aimNumber}>
                        <p className="text-xs font-medium text-gray-500">{aim.aimText}</p>
                        <ul className="mt-1 space-y-1">
                          {aim.goals.map((goal) => {
                            const evidenced = goal.observationCount > 0;
                            return (
                              <li
                                key={goal.goalId}
                                data-testid={`lg-coverage-goal-${goal.goalId}`}
                                className="flex items-start justify-between gap-3 text-sm"
                              >
                                <span className="flex items-start gap-2 text-gray-900">
                                  <span
                                    aria-hidden
                                    className={`mt-0.5 ${
                                      evidenced ? 'text-green-600' : 'text-gray-300'
                                    }`}
                                  >
                                    {evidenced ? '✓' : '○'}
                                  </span>
                                  {goal.text}
                                </span>
                                {evidenced && (
                                  <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                    {goal.observationCount}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
        </>
      )}
    </div>
  );
};

export default LearningGoalCoverage;
